import { useState, useRef, useEffect } from "react";
import {
  Col,
  Row,
  Button,
  FormControl,
  InputGroup,
  Form,
  Container,
} from "react-bootstrap";
import Link from "next/link";
import PhoneInput from "react-phone-number-input";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { customerServices } from "@/services/customerServices";
import { healerServices } from "@/services/healerServices";
import Layout from "@/components/layout";
import Testimonial from "@/components/customer/testimonials";
import "react-phone-number-input/style.css";
import styles from "@/styles/ContactUs.module.css";
import Spinner from "react-bootstrap/Spinner";
import { getCountryCallingCode } from "react-phone-number-input";
import Head from "next/head";

const ContactUs = (props) => {
  const [testimonials, setTestimonial] = useState(props?.testimonials ?? []);
  const [selectedCountry, setSelectedCountry] = useState("US");
  const [apiResponse, setApiResponse] = useState(true);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [contactMeta, setContactMeta] = useState(props?.contactMeta ?? {});
  const [showSideButton, setShowSideButton] = useState(false);
  const firstSectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!firstSectionRef.current) return;

      const sectionTop = firstSectionRef.current.getBoundingClientRect().bottom;

      setShowSideButton(sectionTop < 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    // Clear phone input value when country change
    setFieldValue("phone", "");
  };

  const handlePhoneChange = (value) => {
    const number = value ? value.toString() : "";
    if (isPossiblePhoneNumber(number) && isValidPhoneNumber(number)) {
      setFieldValue("phone", number);
    }
  };

  const initialValues = {
    name: "",
    email: "",
    mobile_number: "",
    subject: "",
    description: "",
  };

  // Yup schema to validate the form
  const validationSchema = Yup.object().shape({
    name: Yup.string().max(255).required("Name is required").label("Name"),
    email: Yup.string()
      .email("Must be a valid email")
      .max(255)
      .required("Email is required")
      .label("Email"),
    subject: Yup.string()
      .max(255)
      .required("Subject is required")
      .label("Subject"),
    description: Yup.string()
      .max(255)
      .required("description is required")
      .label("description"),
    mobile_number: Yup.string()
      .required()
      .test(
        "is-digit",
        "Must be a valid digit between 8 and 14 characters",
        (value) => {
          const countryCode = `+${getCountryCallingCode(selectedCountry)}`;
          // Define your regular expression for validation
          const digitRegex = /^\d{8,14}$/;
          // Test the value against the regular expression
          return digitRegex.test(value.replace(countryCode, ""));
        }
      )
      .label("Mobile Number"),
  });

  // Formik hook to handle the form state
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    // Pass the Yup schema to validate the form
    validationSchema,
    // Handle form submission
    onSubmit: async (payload) => {
      setIsButtonClicked(true);
      const idd = toast.loading("Please wait...");
      const res = await healerServices.contactus({ ...payload });
      setIsButtonClicked(false);
      if (res.status) {
        toast.update(idd, {
          render: res.message,
          type: "success",
          autoClose: 5000,
          isLoading: false,
        });
        handleReset();
      } else {
        toast.update(idd, {
          render: res.message,
          type: "error",
          autoClose: 5000,
          isLoading: false,
        });
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleChange,
    handleSubmit,
    setFieldValue,
    handleReset,
  } = formik;
  return (
    <>
      <Head>
        <title>{contactMeta?.meta?.og_title}</title>
        <meta name="title" content={contactMeta?.meta?.meta_title}></meta>
        <meta
          name="description"
          content={contactMeta?.meta?.meta_description}
        ></meta>
        <meta name="keywords" content={contactMeta?.meta?.meta_keywords}></meta>
        <meta property="og:type" content="website"></meta>
        <meta property="og:title" content={contactMeta?.meta?.og_title}></meta>
        <meta property="og:image" content={contactMeta?.meta?.og_image}></meta>
        <link rel="canonical" href="https://www.zenkify.com/contact-us"></link>
        <meta
          property="og:description"
          content={contactMeta?.meta?.meta_description}
        ></meta>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={contactMeta?.meta?.meta_title} />
        <meta
          name="twitter:description"
          content={contactMeta?.meta?.meta_description}
        />
        <meta name="twitter:image" content={contactMeta?.meta?.og_image} />
        <meta property="og:url" content="https://www.zenkify.com/contact-us" />
        <meta property="og:site_name" content="Zenkify" />
        <meta property="og:image:alt" content="zenkify contact us" />
        <meta property="og:image" content={contactMeta?.meta?.og_image}></meta>
      </Head>
      <section
        className={
          styles["section-banner"] +
          " " +
          styles["banner-page"] +
          " " +
          styles["contact-banner"]
        }
        style={{ background: "url(/images/contact-banner.png)" }}
        ref={firstSectionRef}
      >
        <Container>
          <Row>
            <Col md={8} className="m-auto">
              <div className={styles["inner-content"]}>
                <h1 className={styles["banner-title"]}>Contact Us</h1>
                <p>
                  Our certified practitioners are here to guide you on your
                  journey to wellness and balance. Reach out today to schedule a
                  session or ask any questions. Your well- being is our
                  priority, and we&apos;re here to support you on your path to
                  healing.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <section
        className={styles["contact-section"]}
        style={{ background: "url(/images/contact-bg.jpg)" }}
      >
        <Container>
          <Row>
            <Col lg={7} className="m-auto">
              <div className="section-title">
                <h2 className="title">How can we help you?</h2>
                <p>
                  We are here to support your well-being journey. Let us know
                  how we can assist you with energy healing, holistic wellness,
                  or any inquiries you may have. Please provide your query or
                  request below, and our team will promptly address your needs.
                  Your satisfaction is important to us, and we&apos;re committed
                  to providing guidance and resources tailored to your needs.
                </p>
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={8} className="m-auto">
              <div className={styles["contact-formbox"]}>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6} className="mb-3">
                      <InputGroup>
                        <InputGroup.Text className={styles["input-group-text"]}>
                          <i className="ri-user-3-line"></i>
                        </InputGroup.Text>

                        <FormControl
                          type="text"
                          name="name"
                          onChange={handleChange}
                          value={values.name}
                          className={styles["form-control"]}
                          placeholder="Name*"
                        />
                      </InputGroup>
                      <span>
                        {touched.name && errors.name ? (
                          <div className="text-danger">{errors.name}</div>
                        ) : null}
                      </span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <InputGroup>
                        <InputGroup.Text className={styles["input-group-text"]}>
                          <i className="ri-mail-line"></i>
                        </InputGroup.Text>

                        <FormControl
                          type="email"
                          name="email"
                          onChange={handleChange}
                          value={values.email}
                          className={styles["form-control"]}
                          placeholder="Email Address*"
                        />
                      </InputGroup>
                      <span>
                        {touched.email && errors.email ? (
                          <div className="text-danger">{errors.email}</div>
                        ) : null}
                      </span>
                    </Col>
                    <Col
                      md={6}
                      className={`${styles["phoneinput-group"] + " " + "mb-3"}`}
                    >
                      <PhoneInput
                        placeholder="Enter Mobile Number"
                        name="mobile_number"
                        className={`${
                          styles["phoneinput"] + " " + "form-control"
                        }`}
                        international
                        countryCallingCodeEditable={false}
                        defaultCountry="US"
                        value={values.mobile_number}
                        onChange={(value) =>
                          setFieldValue("mobile_number", value)
                        }
                        onCountryChange={setSelectedCountry}
                        //limitMaxLength={true}
                      />
                      <span>
                        {touched.mobile_number && errors.mobile_number ? (
                          <div className="text-danger">
                            {errors.mobile_number}
                          </div>
                        ) : null}
                      </span>
                    </Col>
                    <Col md={6} className="mb-3">
                      <InputGroup>
                        <InputGroup.Text className={styles["input-group-text"]}>
                          <i className="ri-send-plane-line"></i>
                        </InputGroup.Text>

                        <FormControl
                          type="text"
                          name="subject"
                          onChange={handleChange}
                          value={values.subject}
                          className={styles["form-control"]}
                          placeholder="Subject"
                        />
                      </InputGroup>
                      <span>
                        {touched.subject && errors.subject ? (
                          <div className="text-danger">{errors.subject}</div>
                        ) : null}
                      </span>
                    </Col>
                    <Col md={12} className="mb-3">
                      <InputGroup>
                        <InputGroup.Text
                          className={`${styles["input-group-text"]} align-items-start`}
                        >
                          <i className="ri-chat-4-line"></i>
                        </InputGroup.Text>
                        <textarea
                          name="description"
                          onChange={handleChange}
                          value={values.description}
                          className={styles["form-control"]}
                          id=""
                          rows="5"
                          placeholder="Type Here"
                        ></textarea>
                      </InputGroup>
                      <span>
                        {touched.description && errors.description ? (
                          <div className="text-danger">
                            {errors.description}
                          </div>
                        ) : null}
                      </span>
                    </Col>
                    <Col md={12} className="text-center">
                      <Button
                        type="submit"
                        className="theme-btn"
                        onClick={() => setApiResponse(false)}
                        disabled={isButtonClicked}
                      >
                        Submit <i className="ri-arrow-right-line"></i>
                      </Button>
                    </Col>
                  </Row>
                </Form>
                <div className={styles["contact-footer"]}>
                  <div className={styles["email"]}>
                    <i className="ri-mail-line"></i>
                    <Link href="mailto:hello@zenkify.com">
                      hello@zenkify.com
                    </Link>
                    {/* <i className="ri-phone-line"></i>
                    <Link href="tel:+1(208) 407-9559">+1(208) 407-9559</Link> */}
                  </div>
                  <ul>
                    <li>
                      <Link
                        href="https://www.facebook.com/ZenkifyWorldwide"
                        target="blank"
                      >
                        <i className="ri-facebook-fill"></i>
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="https://www.instagram.com/zenkify_worldwide/"
                        target="blank"
                      >
                        <i className="ri-instagram-fill"></i>
                      </Link>
                    </li>
                    {/* <li><Link href="#"><i className="ri-youtube-fill"></i></Link></li>
                    <li>
                      <Link href="#">
                        <i className="ri-linkedin-fill"></i>
                      </Link>
                    </li> */}
                    <li>
                      <Link href="https://twitter.com/Zenkify" target="blank">
                        <i className="ri-twitter-x-line"></i>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      <Testimonial props={{ testimonials }} />
      <Link
        id="floatingBook"
        className="floating-book"
        aria-label="Book a FREE call now"
        href="/healers"
        style={{ opacity: showSideButton ? 1 : 0 }}
      >
        Book a FREE call now
      </Link>
    </>
  );
};

export default ContactUs;

ContactUs.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

// ssr
export async function getServerSideProps() {
  const fetchTestimonials = async () => {
    const res = await customerServices.getTestimonials();
    if (res.status) {
      return res.data;
    } else {
      toast.error(res.message, {
        autoClose: 5000,
      });
    }
  };

  const fetchContactMeta = async () => {
    try {
      const res = await healerServices.getMetaInformation("contact-us");
      if (res.status) {
        return res.data;
      }
    } catch (error) {
      console.error("Error fetching slider data:", error);
    }
  };

  const testimonials = (await fetchTestimonials()) ?? {};
  const contactMeta = (await fetchContactMeta()) ?? {};

  // Pass data to the page via props
  return { props: { testimonials, contactMeta } };
}
